import { t } from "@travily/i18n";
import type TripsRow from "@travily/types/models/TripsRow";
import type { TripsRowCanTrait } from "@travily/types/models/TripsRow";
import type { ConfirmDialogState } from "@ui/components/ConfirmDialog";
import { DeleteConfirmDialog } from "@ui/components/DeleteConfirmDialog";
import { EmptyState } from "@ui/components/EmptyState";
import { FullBleed } from "@ui/components/FullBleed";
import { Heading } from "@ui/components/Heading";
import { Illustration } from "@ui/components/Illustration";
import { OutlineButton } from "@ui/components/OutlineButton";
import { TripCard } from "./TripCard";
import type { TripOwner } from "./types";

type TripWithCan = TripsRow & TripsRowCanTrait;

export interface TripsProps {
  canCreate?: boolean;
  deleteState: ConfirmDialogState;
  onCreateTrip?: () => void;
  onDeleteTrip: (sqid: string) => void;
  onEditTrip: (trip: TripWithCan) => void;
  owner: TripOwner;
  trips: TripWithCan[];
}

export function Trips({ canCreate, deleteState, onCreateTrip, onDeleteTrip, onEditTrip, owner, trips }: TripsProps) {
  return (
    <div className="flex w-full flex-col gap-5 lg:gap-8 md:text-left">
      <Heading.Root divider="responsive">
        <Heading.Title>{t("components.views.shared.trips.title")}</Heading.Title>
        {canCreate ? (
          <Heading.Actions>
            <OutlineButton className="md:hidden" onPress={onCreateTrip} size="36">
              {t("components.views.shared.trips.add")}
            </OutlineButton>
            <OutlineButton className="hidden md:inline-flex lg:hidden" onPress={onCreateTrip} size="36">
              {t("components.views.shared.trips.add_trip")}
            </OutlineButton>
            <OutlineButton className="hidden lg:inline-flex" onPress={onCreateTrip}>
              {t("components.views.shared.trips.add_trip")}
            </OutlineButton>
          </Heading.Actions>
        ) : null}
      </Heading.Root>
      {trips.length > 0 ? (
        <FullBleed className="md:contents">
          <div className="flex flex-col gap-8">
            {trips.map((trip) => (
              <TripCard
                owner={owner}
                key={trip.sqid}
                onDelete={() => onDeleteTrip(trip.sqid)}
                onEdit={() => onEditTrip(trip)}
                trip={trip}
              />
            ))}
          </div>
        </FullBleed>
      ) : (
        <EmptyState
          description={
            canCreate
              ? t("components.views.shared.trips.empty_owner")
              : t("components.views.shared.trips.empty_visitor", { name: owner.display_name })
          }
          illustration={<Illustration name="trips" />}
          title={t("components.views.shared.trips.empty_title")}
        />
      )}
      <DeleteConfirmDialog
        description={t("components.views.shared.trips.delete_confirmation")}
        isPending={deleteState.isPending}
        onConfirm={deleteState.onConfirm}
        onOpenChange={deleteState.onOpenChange}
        isOpen={deleteState.isOpen}
      />
    </div>
  );
}
